/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiHorizontalRule,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiStepsHorizontal,
} from "@inscopix/ideas-eui";
import { ReactNode, useState } from "react";
import { StepTableInfo } from "./steps/StepTableInfo";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { StepParamMapping } from "./steps/StepParamMapping";
import { StepJoinColumns } from "./steps/StepJoinColumns";
import { StepSummary } from "./steps/StepSummary";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { keyBy } from "lodash";
import { isNonNullish } from "utils/isNonNullish";
import { css } from "@emotion/react";

type Fields = {
  toolSpec: {
    value: ToolSpec | undefined;
    isValid: boolean;
  };
  toolVersionId: {
    value: number | undefined;
    isValid: boolean;
  };
  tableName: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  tableKey: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  columnGroupName: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  sourceTableIds: {
    value: string[];
    isValid: boolean;
  };
  paramMap: {
    value: {
      [paramKey: string]:
        | {
            tableId: string | undefined;
            columnId: string | undefined;
          }
        | undefined;
    };
    isValid: boolean;
    isDirty: boolean;
  };
  paramKeys: {
    value: string[];
    isValid: boolean;
    isDirty: boolean;
  };
  autoPopulateRows: {
    value: boolean;
    isValid: boolean;
  };
};

const INITIAL_FIELDS: Fields = {
  toolSpec: {
    value: undefined,
    isValid: false,
  },
  toolVersionId: {
    value: undefined,
    isValid: false,
  },
  tableName: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  tableKey: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  columnGroupName: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  sourceTableIds: {
    value: [],
    isValid: true,
  },
  paramMap: {
    value: {},
    isValid: true,
    isDirty: false,
  },
  paramKeys: {
    value: [],
    isValid: true,
    isDirty: false,
  },
  autoPopulateRows: {
    value: true,
    isValid: true,
  },
};

type Step = {
  isComplete: boolean;
  node: ReactNode;
};

interface ModalJoinAnalysisTableNewProps {
  dataTableId?: string;
  folderId?: string | null;
  onBack?: () => void;
  onClose: () => void;
}

/**
 * Component that renders a modal for creating analysis tables
 * Optionally takes a `dataTableId` to create an analysis table from an existing data table
 * and/or a `folder` to create the analysis table in a specific folder.
 */
export const ModalCreateAnalysisTable = ({
  dataTableId,
  folderId,
  onBack,
  onClose,
}: ModalJoinAnalysisTableNewProps) => {
  const tablesById = useDataTableContext((s) =>
    keyBy(s.tables, ({ id }) => id),
  );
  const createAnalysisTable = useDataTableContext((s) => s.createAnalysisTable);
  const createAnalysisTableFromDataTable = useDataTableContext(
    (s) => s.createAnalysisTableFromDataTable,
  );
  const setSelectedTableId = useDataTableContext((s) => s.setSelectedTableId);

  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState(INITIAL_FIELDS);

  const steps: Step[] = [
    {
      isComplete:
        fields.toolSpec.isValid &&
        fields.toolVersionId.isValid &&
        fields.tableName.isValid &&
        fields.tableKey.isValid,
      node: (
        <StepTableInfo
          key="table-info"
          toolSpec={fields.toolSpec.value}
          setToolSpec={(toolSpec) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.toolSpec = {
                value: toolSpec,
                isValid: toolSpec !== undefined,
              };
              newFields.paramMap = {
                value: {},
                isValid: true,
                isDirty: false,
              };
              newFields.paramKeys = {
                value: [],
                isValid: true,
                isDirty: false,
              };
              return newFields;
            });
          }}
          setToolVersionId={(toolVersionId) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.toolVersionId = {
                value: toolVersionId,
                isValid: toolVersionId !== undefined,
              };
              return newFields;
            });
          }}
          tableName={fields.tableName}
          setTableName={(tableName, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.tableName = {
                value: tableName,
                isDirty,
                isValid: tableName !== "",
              };
              return newFields;
            });
          }}
          tableKey={fields.tableKey}
          setTableKey={(tableKey, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.tableKey = {
                value: tableKey,
                isValid: tableKey !== "",
                isDirty,
              };
              return newFields;
            });
          }}
          paramKeys={fields.paramKeys}
          setParamKeys={(paramKeys, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.paramKeys = {
                value: paramKeys,
                isDirty,
                isValid: true,
              };
              return newFields;
            });
          }}
          columnGroupName={fields.columnGroupName}
          setColumnGroupName={(columnGroupName, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.columnGroupName = {
                value: columnGroupName,
                isDirty,
                isValid: columnGroupName !== "",
              };
              return newFields;
            });
          }}
        />
      ),
    },

    {
      isComplete: fields.paramMap.isDirty,
      node: fields.toolSpec.value && (
        <StepParamMapping
          key="param-mapping"
          toolSpec={fields.toolSpec.value}
          dataTableId={dataTableId}
          sourceTableIds={fields.sourceTableIds.value}
          setSourceTableIds={(tableIds) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.sourceTableIds = {
                value: tableIds,
                isValid: true,
              };
              return newFields;
            });
          }}
          paramMap={fields.paramMap}
          setParamMap={(paramMap) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.paramMap = {
                value: paramMap,
                isValid: true,
                isDirty: true,
              };
              return newFields;
            });
          }}
        />
      ),
    },

    // Only show this step if we have a data table to join back to
    ...(dataTableId !== undefined
      ? [
          {
            isComplete: fields.paramKeys.isDirty,
            node: fields.toolSpec.value && (
              <StepJoinColumns
                toolSpec={fields.toolSpec.value}
                columnGroupName={fields.columnGroupName}
                paramKeys={fields.paramKeys}
                setParamKeys={(paramKeys, isDirty) => {
                  setFields((prevFields) => {
                    const newFields = { ...prevFields };
                    newFields.paramKeys = {
                      value: paramKeys,
                      isValid: true,
                      isDirty,
                    };
                    return newFields;
                  });
                }}
                setColumnGroupName={(columnGroupName, isDirty) => {
                  setFields((prevFields) => {
                    const newFields = { ...prevFields };
                    newFields.columnGroupName = {
                      value: columnGroupName,
                      isDirty,
                      isValid: columnGroupName !== "",
                    };
                    return newFields;
                  });
                }}
              />
            ),
          },
        ]
      : []),

    {
      isComplete: false,
      node: fields.toolSpec.value && (
        <StepSummary
          toolSpec={fields.toolSpec.value}
          tableName={fields.tableName}
          tableKey={fields.tableKey.value}
          paramMap={fields.paramMap.value}
          columnGroupName={fields.columnGroupName}
          dataTableId={dataTableId}
          paramKeys={fields.paramKeys.value}
          autoPopulateRows={fields.autoPopulateRows.value}
          setTableName={(tableName, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.tableName = {
                value: tableName,
                isDirty,
                isValid: tableName !== "",
              };
              return newFields;
            });
          }}
          setTableKey={(tableKey) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.tableKey = {
                value: tableKey,
                isValid: tableKey !== "",
                isDirty: true,
              };
              return newFields;
            });
          }}
          setAutoPopulateRows={(autoPopulateRows) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.autoPopulateRows = {
                value: autoPopulateRows,
                isValid: true,
              };
              return newFields;
            });
          }}
          setColumnGroupName={(columnGroupName, isDirty) => {
            setFields((prevFields) => {
              const newFields = { ...prevFields };
              newFields.columnGroupName = {
                value: columnGroupName,
                isDirty,
                isValid: columnGroupName !== "",
              };
              return newFields;
            });
          }}
        />
      ),
    },
  ];

  const handleSubmit = async () => {
    // Create new analysis table
    if (dataTableId === undefined) {
      try {
        setIsLoading(true);
        assert(isDefined(fields.toolVersionId.value));
        const { data: analysisTableCreatedId } = await createAnalysisTable({
          name: fields.tableName.value,
          key: fields.tableKey.value,
          toolVersionId: fields.toolVersionId.value,
          sourceTables: fields.sourceTableIds.value.map((id) => ({
            id,
            kind:
              tablesById[id].kind === "data"
                ? "data_table"
                : "gdt_analysis_table",
          })),
          paramMap: Object.entries(fields.paramMap.value).map(
            ([param, mapping]) => ({
              param,
              column: {
                id: mapping?.columnId as string,
                kind:
                  tablesById[mapping?.tableId as string].kind === "data"
                    ? "data_table_column"
                    : "gdt_analysis_table_column",
              },
            }),
          ),
          // if a folder is provided (creating a table from a folder's action menu), use it
          // else if only one source table, set the folder to that table's folder (if it exists)
          folder: isNonNullish(folderId)
            ? folderId
            : fields.sourceTableIds.value.length === 1
            ? tablesById[fields.sourceTableIds.value[0]].folder
            : null,
        });
        setIsLoading(false);
        addUtilityToastSuccess("Analysis table created");
        if (analysisTableCreatedId !== undefined) {
          setSelectedTableId(analysisTableCreatedId);
        }
        onClose();
      } catch (error) {
        addUtilityToastFailure("Failed to create analysis table");
        setIsLoading(false);
      }
    }
    // Create new analysis table from current data table
    else {
      try {
        setIsLoading(true);
        assert(isDefined(fields.toolVersionId.value));
        const analysisTableCreatedId = await createAnalysisTableFromDataTable({
          dataTableId,
          analysisTableName: fields.tableName.value,
          analysisTableKey: fields.tableKey.value,
          toolVersionId: fields.toolVersionId.value,
          autoPopulateRows: fields.autoPopulateRows.value,
          paramMap: Object.entries(fields.paramMap.value).map(
            ([param, mapping]) => ({
              param,
              column: {
                id: mapping?.columnId as string,
                kind: "data_table_column",
              },
            }),
          ),
          columnGroupName: fields.columnGroupName.value,
          paramKeys: fields.paramKeys.value,
        });
        setIsLoading(false);
        addUtilityToastSuccess("Analysis table created");
        setSelectedTableId(analysisTableCreatedId);
        onClose();
      } catch (error) {
        addUtilityToastFailure("Failed to create analysis table");
        setIsLoading(false);
      }
    }
  };

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 900px;
        max-width: 900px;
      `}
    >
      <EuiModalHeader>
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiModalHeaderTitle component="h3">
            Create Analysis Table
          </EuiModalHeaderTitle>

          <EuiStepsHorizontal
            steps={Object.values(steps).map(({ isComplete }, idx) => {
              let status;
              if (step === idx) {
                status = "current" as const;
              } else if (idx > 0 && !steps[idx - 1].isComplete) {
                status = "disabled" as const;
              } else if (isComplete) {
                status = "complete" as const;
              } else {
                status = "incomplete" as const;
              }

              return {
                status,
                disabled: status === "disabled",
                onClick: () => setStep(idx),
              };
            })}
            size="m"
          />

          <EuiHorizontalRule margin="none" />
        </EuiFlexGroup>
      </EuiModalHeader>

      <EuiModalBody>{steps[step].node}</EuiModalBody>

      <EuiModalFooter>
        {(onBack || step > 0) && (
          <EuiButtonEmpty
            iconType="arrowLeft"
            onClick={() => {
              if (step === 0) {
                onBack?.();
              } else {
                setStep((step) => step - 1);
              }
            }}
          >
            Back
          </EuiButtonEmpty>
        )}

        {step < steps.length - 1 ? (
          <EuiButton
            fill
            disabled={!steps[step].isComplete}
            onClick={() => setStep((step) => step + 1)}
          >
            Next
          </EuiButton>
        ) : (
          <EuiButton
            isLoading={isLoading}
            fill
            disabled={Object.values(fields).some((f) => !f.isValid)}
            onClick={() => void handleSubmit()}
          >
            Create
          </EuiButton>
        )}
      </EuiModalFooter>
    </EuiModal>
  );
};
